// ===================================
// BOOKING MODULE - FRONTEND
// Professional booking form handler for GoPeruNow
// ===================================

import { apiService } from "./api-service.js";
import { notifications } from "./notifications.js";
import { validators } from "./validators.js";
import { CULQI_PUBLIC_KEY, isCulqiConfigured } from "./culqi-config.js";

class BookingManager {
  constructor() {
    this.form = null;
    this.submitBtn = null;
    this.btnText = null;
    this.btnLoading = null;
    this.messageContainer = null;
    this.isSubmitting = false;

    // Set right before opening Culqi's checkout, consumed once the
    // tokenization callback (`window.culqi`) fires.
    this.pendingBookingData = null;
    this.pendingPrice = null;

    // Configuration
    this.config = {
      minDate: this.getTomorrowDate(),
      maxGuests: 20,
      tourPrices: {
        "3-Hour Ride | express Horseback Tour in Cusco": 50.0,
        "4-Hour Ride | Cultural Horseback Experience in Cusco": 65.0,
        "Half-Day Ride | Panoramic Horseback Tour in Cusco": 75.0,
        "Apu Katunki | Sacred Mountain Horseback Adventure": 85.0,
        "Yanaqocha & Qeullaqocha | Lakes and Llamas Cultural Ride": 95.0,
        "Huchuy Qosqo | Full Inca Horseback Expedition": 220.0,
      },
    };
  }

  /**
   * Initialize the booking form
   */
  init() {
    this.findElements();
    if (this.form) {
      this.preselectTourFromQuery();
      this.setupEventListeners();
      this.setupFormValidation();
      this.setupPriceCalculation();
      this.setMinDate();
      this.setupCardFields();

      // Culqi Checkout calls a global function literally named `culqi`
      // once the guest finishes entering their card in its hosted iframe.
      window.culqi = () => this.handleCulqiResponse();

      console.log("BookingManager initialized successfully");
    }
  }

  /**
   * Pre-select a tour when arriving via a "Reserva Ahora" link
   * (e.g. book-now.html?tour=tour_3h) so guests land with their
   * chosen adventure already checked and priced.
   */
  preselectTourFromQuery() {
    const tourId = new URLSearchParams(window.location.search).get("tour");
    if (!tourId) return;

    const tourInputs = this.form.querySelectorAll('input[name="tour"]');
    const match = Array.from(tourInputs).find((input) => input.id === tourId);
    if (match) {
      match.checked = true;
    }
  }

  /**
   * Find DOM elements
   */
  findElements() {
    this.form = document.getElementById("booking-form");
    this.submitBtn = document.getElementById("submit-btn");
    this.btnText = document.getElementById("btn-text");
    this.btnLoading = document.getElementById("btn-loading");
    this.messageContainer = document.getElementById("form-message");

    // Card fields live only in the DOM — none of them carry a `name`
    // attribute, so they can never end up in collectFormData()/FormData()
    // and are never sent anywhere except straight into Culqi.createToken().
    this.cardNumberInput = document.getElementById("card-number");
    this.cardExpMonthSelect = document.getElementById("card-exp-month");
    this.cardExpYearSelect = document.getElementById("card-exp-year");
    this.cardCvvInput = document.getElementById("card-cvv");
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Form submission
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));

    // Real-time validation
    const inputs = this.form.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      input.addEventListener("blur", () => this.validateField(input));
      input.addEventListener("input", () => this.clearFieldError(input));
    });

    // Tour selection change for price update
    const tourInputs = this.form.querySelectorAll('input[name="tour"]');
    tourInputs.forEach((input) => {
      input.addEventListener("change", () => this.updatePriceEstimate());
    });

    // Guest count change for price update
    const guestsSelect = document.getElementById("guests");
    if (guestsSelect) {
      guestsSelect.addEventListener("change", () => this.updatePriceEstimate());
    }
  }

  /**
   * Setup form validation
   */
  setupFormValidation() {
    const requiredFields = this.form.querySelectorAll("[required]");
    requiredFields.forEach((field) => {
      field.addEventListener("invalid", (e) => {
        e.preventDefault();
        this.showFieldError(field, false, "Este campo es obligatorio");
      });
    });
  }

  /**
   * Setup price calculation display
   */
  setupPriceCalculation() {
    // Create price display if it doesn't exist
    let priceDisplay = document.getElementById("price-estimate");
    if (!priceDisplay) {
      priceDisplay = document.createElement("div");
      priceDisplay.id = "price-estimate";
      priceDisplay.className =
        "mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg";
      priceDisplay.innerHTML = `
                <h4 class="font-semibold text-amber-800 mb-2">Resumen del Pago</h4>
                <div id="price-breakdown" class="text-sm text-amber-700"></div>
            `;

      // Insert before submit button
      const submitContainer = this.submitBtn.closest(".pt-4");
      if (submitContainer) {
        submitContainer.insertBefore(priceDisplay, submitContainer.firstChild);
      }
    }

    this.updatePriceEstimate();
  }

  /**
   * Compute the price for the currently selected tour + guest count.
   * This is the single source of truth for pricing — both the on-screen
   * estimate and the amount actually sent to Culqi come from here, so the
   * two can never drift apart.
   */
  calculatePrice() {
    const selectedTour = this.form.querySelector('input[name="tour"]:checked');
    const guestsSelect = document.getElementById("guests");

    const tourName = selectedTour ? selectedTour.value : "";
    const guestCount = guestsSelect ? parseInt(guestsSelect.value) || 2 : 2;
    const basePrice = this.config.tourPrices[tourName] || 0;

    // Calculate discount
    let discount = 0;
    if (guestCount >= 5) {
      discount = 0.15; // 15% group discount
    } else if (guestCount >= 3) {
      discount = 0.08; // 8% small group discount
    }

    const subtotal = basePrice * guestCount;
    const discountAmount = subtotal * discount;
    const total = subtotal - discountAmount;

    return {
      tourName,
      guestCount,
      basePrice,
      discount,
      subtotal,
      discountAmount,
      total,
    };
  }

  /**
   * Update price estimate
   */
  updatePriceEstimate() {
    const priceBreakdown = document.getElementById("price-breakdown");
    if (!priceBreakdown) return;

    const {
      tourName,
      guestCount,
      basePrice,
      discount,
      subtotal,
      discountAmount,
      total,
    } = this.calculatePrice();

    if (!tourName) return;

    let breakdown = `
            <div class="flex justify-between">
                <span>${tourName.split("|")[0].trim()}</span>
                <span>$${basePrice.toFixed(2)} × ${guestCount}</span>
            </div>
            <div class="flex justify-between">
                <span>Subtotal</span>
                <span>$${subtotal.toFixed(2)}</span>
            </div>
        `;

    if (discount > 0) {
      breakdown += `
                <div class="flex justify-between text-green-600">
                    <span>Descuento por grupo (${(discount * 100).toFixed(
                      0
                    )}%)</span>
                    <span>-$${discountAmount.toFixed(2)}</span>
                </div>
            `;
    }

    breakdown += `
            <div class="flex justify-between font-bold text-lg border-t border-amber-300 pt-2 mt-2">
                <span>Total a pagar</span>
                <span>$${total.toFixed(2)} USD</span>
            </div>
            <div class="text-xs mt-2 text-amber-600">
                * Este monto se cobrará de inmediato a tu tarjeta al confirmar la reserva
            </div>
        `;

    priceBreakdown.innerHTML = breakdown;
  }

  /**
   * Set minimum date to tomorrow
   */
  setMinDate() {
    const dateInput = document.getElementById("date");
    if (dateInput) {
      dateInput.min = this.config.minDate;
      dateInput.value = this.config.minDate; // Set default value
    }
  }

  /**
   * Get tomorrow's date in YYYY-MM-DD format
   */
  getTomorrowDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  }

  /**
   * Populate the expiration month/year selects and keep the card number /
   * CVV inputs digits-only as the guest types.
   */
  setupCardFields() {
    if (this.cardExpMonthSelect) {
      for (let month = 1; month <= 12; month++) {
        const value = String(month).padStart(2, "0");
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        this.cardExpMonthSelect.appendChild(option);
      }
    }

    if (this.cardExpYearSelect) {
      const currentYear = new Date().getFullYear();
      for (let year = currentYear; year <= currentYear + 15; year++) {
        const option = document.createElement("option");
        option.value = String(year);
        option.textContent = String(year);
        this.cardExpYearSelect.appendChild(option);
      }
    }

    if (this.cardNumberInput) {
      this.cardNumberInput.addEventListener("input", () => {
        const digits = this.cardNumberInput.value
          .replace(/\D/g, "")
          .slice(0, 16);
        this.cardNumberInput.value = digits.replace(/(\d{4})(?=\d)/g, "$1 ");
      });
    }

    if (this.cardCvvInput) {
      this.cardCvvInput.addEventListener("input", () => {
        this.cardCvvInput.value = this.cardCvvInput.value
          .replace(/\D/g, "")
          .slice(0, 4);
      });
    }
  }

  /**
   * Validate individual field
   */
  validateField(field) {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = "";

    // Required field check
    if (field.hasAttribute("required") && !value) {
      isValid = false;
      errorMessage = "Este campo es obligatorio";
    } else if (value) {
      // Type-specific validation
      switch (field.type) {
        case "email":
          if (!validators.isValidEmail(value)) {
            isValid = false;
            errorMessage = "Ingresa un correo electrónico válido";
          }
          break;
        case "tel":
          if (!validators.isValidPhone(value)) {
            isValid = false;
            errorMessage = "Ingresa un número de teléfono válido";
          }
          break;
        case "date":
          if (!validators.isValidFutureDate(value)) {
            isValid = false;
            errorMessage = "Selecciona una fecha futura";
          }
          break;
      }

      // Custom validation rules
      if (field.id === "first_name" || field.id === "last_name") {
        if (value.length < 2) {
          isValid = false;
          errorMessage = "El nombre debe tener al menos 2 caracteres";
        }
      } else if (field.id === "card-number") {
        const digits = value.replace(/\s/g, "");
        if (digits.length < 13 || digits.length > 19 || !/^\d+$/.test(digits)) {
          isValid = false;
          errorMessage = "Ingresa un número de tarjeta válido";
        }
      } else if (field.id === "card-cvv") {
        if (!/^\d{3,4}$/.test(value)) {
          isValid = false;
          errorMessage = "CVV inválido";
        }
      }
    }

    this.showFieldError(field, isValid, errorMessage);
    return isValid;
  }

  /**
   * Show field error
   */
  showFieldError(field, isValid, message) {
    // Update field styling
    field.classList.toggle("border-red-500", !isValid);
    field.classList.toggle("border-green-500", isValid && field.value.trim());
    field.classList.toggle("border-stone-300", isValid && !field.value.trim());

    // Handle error message
    let errorElement = field.parentNode.querySelector(".field-error");

    if (!isValid && message) {
      if (!errorElement) {
        errorElement = document.createElement("div");
        errorElement.className =
          "field-error text-red-500 text-sm mt-1 flex items-center";
        errorElement.innerHTML = `
                    <svg class="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    <span class="error-text"></span>
                `;
        field.parentNode.appendChild(errorElement);
      }
      errorElement.querySelector(".error-text").textContent = message;
    } else if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * Clear field error
   */
  clearFieldError(field) {
    field.classList.remove("border-red-500");
    const errorElement = field.parentNode.querySelector(".field-error");
    if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * Collect form data
   */
  collectFormData() {
    const formData = new FormData(this.form);
    const data = {
      // Tour selection
      tour: formData.get("tour"),
      date: formData.get("date"),
      guests: parseInt(formData.get("guests")),

      // Personal information
      first_name: formData.get("first_name")?.trim(),
      last_name: formData.get("last_name")?.trim(),
      email: formData.get("email")?.trim().toLowerCase(),
      phone: formData.get("phone")?.trim(),

      // Experience and preferences
      experience: formData.get("experience"),
      message: formData.get("message")?.trim() || "",

      // Metadata
      submission_time: new Date().toISOString(),
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      page_url: window.location.href,
      timestamp: Date.now(),
    };

    return data;
  }

  /**
   * Validate entire form
   */
  validateForm() {
    const requiredFields = this.form.querySelectorAll("[required]");
    let isFormValid = true;

    requiredFields.forEach((field) => {
      if (!this.validateField(field)) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  /**
   * Handle form submission: validate, then tokenize the card fields on
   * this page via Culqi. The actual charge + booking confirmation happen
   * in handleCulqiResponse() / finalizeBookingWithPayment() once Culqi
   * returns a token.
   */
  async handleSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    if (!this.validateForm()) {
      notifications.show(
        "Por favor corrige los errores antes de continuar",
        "error"
      );
      this.focusFirstError();
      return;
    }

    if (typeof Culqi === "undefined") {
      notifications.show(
        "El sistema de pagos aún se está cargando. Intenta de nuevo en unos segundos.",
        "error"
      );
      return;
    }

    if (!isCulqiConfigured()) {
      console.error(
        "CULQI_PUBLIC_KEY is not set — see src/js/culqi-config.js"
      );
      notifications.show(
        "El pago con tarjeta aún no está configurado. Contáctanos al +51 982 373 883 para completar tu reserva.",
        "error"
      );
      return;
    }

    // Snapshot the form + price now, before handing control over to Culqi's
    // modal — we'll need both once the tokenization callback fires.
    this.pendingBookingData = this.collectFormData();
    this.pendingPrice = this.calculatePrice();

    if (!this.pendingPrice.total || this.pendingPrice.total <= 0) {
      notifications.show(
        "Selecciona un tour válido antes de continuar con el pago",
        "error"
      );
      return;
    }

    this.tokenizeCard();
  }

  /**
   * Send the card fields on this page to Culqi for tokenization.
   * Culqi.createToken() posts them straight to Culqi over TLS and, once it
   * responds, calls the global `culqi()` function we wired up in init() —
   * the raw card number/CVV are read here only long enough to hand them to
   * Culqi and are never sent to, or stored on, our own servers.
   */
  tokenizeCard() {
    Culqi.publicKey = CULQI_PUBLIC_KEY;

    const card = {
      card_number: this.cardNumberInput.value.replace(/\s/g, ""),
      cvv: this.cardCvvInput.value.trim(),
      expiration_month: this.cardExpMonthSelect.value,
      expiration_year: this.cardExpYearSelect.value,
      email: this.pendingBookingData.email,
    };

    this.setLoadingState(true);
    Culqi.createToken(card);
  }

  /**
   * Global `culqi()` callback target — fires once Culqi finishes (or
   * rejects) tokenizing the card fields above.
   */
  handleCulqiResponse() {
    if (window.Culqi.token) {
      this.finalizeBookingWithPayment(window.Culqi.token.id);
    } else if (window.Culqi.error) {
      this.setLoadingState(false);
      notifications.show(
        window.Culqi.error.user_message ||
          "No pudimos procesar tu tarjeta. Verifica los datos e intenta nuevamente.",
        "error"
      );
    } else {
      this.setLoadingState(false);
    }
  }

  /**
   * Send the one-time Culqi token (never the card itself) to our backend,
   * which charges it server-side and, on success, confirms the booking.
   */
  async finalizeBookingWithPayment(culqiToken) {
    this.isSubmitting = true;
    this.setLoadingState(true);

    try {
      const payload = {
        ...this.pendingBookingData,
        culqi_token: culqiToken,
        amount: this.pendingPrice.total,
        currency_code: "USD",
      };

      const response = await apiService.createBookingWithPayment(payload);
      notifications.show(response.message, "success");
      this.handleBookingSuccess(response);
    } catch (error) {
      console.error("Payment/booking error:", error);
      notifications.show(
        error.message ||
          "No pudimos procesar tu pago. Intenta de nuevo o contáctanos al +51 982 373 883.",
        "error"
      );
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }

  /**
   * Handle a successful, paid booking
   */
  handleBookingSuccess(response) {
    // Show confirmation before resetting, since reset() would otherwise
    // clear the fields we still want to reflect in the summary below.
    this.showSuccessDetails(response);

    this.form.reset();
    this.setMinDate();
    this.updatePriceEstimate();

    const fields = this.form.querySelectorAll("input, select, textarea");
    fields.forEach((field) => this.clearFieldError(field));

    this.pendingBookingData = null;
    this.pendingPrice = null;
  }

  /**
   * Show a detailed confirmation built from the booking data we already
   * hold client-side (the backend only returns {success, message,
   * booking_id} — see backend/app/main.py).
   */
  showSuccessDetails(response) {
    const data = this.pendingBookingData || {};
    const price = this.pendingPrice || { total: 0 };
    const tourLabel = (data.tour || "").split("|")[0].trim();

    const detailsHtml = `
            <div class="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
                <div class="flex items-center mb-4">
                    <svg class="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <div>
                        <h3 class="text-lg font-bold text-green-800">¡Pago y Reserva Confirmados!</h3>
                        ${
                          response.booking_id
                            ? `<p class="text-green-700">ID de reserva: <strong>${response.booking_id}</strong></p>`
                            : ""
                        }
                    </div>
                </div>

                <div class="bg-white rounded p-4 mb-4">
                    <h4 class="font-semibold text-gray-800 mb-2">Detalles de tu Aventura:</h4>
                    <div class="text-sm text-gray-600 space-y-1">
                        <p><strong>Tour:</strong> ${tourLabel}</p>
                        <p><strong>Fecha:</strong> ${data.date || "-"}</p>
                        <p><strong>Jinetes:</strong> ${data.guests || "-"}</p>
                        <p><strong>Total pagado:</strong> $${price.total.toFixed(
                          2
                        )} USD</p>
                    </div>
                </div>

                <div class="bg-blue-50 border border-blue-200 rounded p-4">
                    <h4 class="font-semibold text-blue-800 mb-2">Próximos Pasos:</h4>
                    <ul class="text-sm text-blue-700 space-y-1">
                        <li>• Te enviaremos la confirmación a ${
                          data.email || "tu correo"
                        }</li>
                        <li>• Nuestro equipo se pondrá en contacto para coordinar los últimos detalles</li>
                        <li>• Guarda tu ID de reserva por si necesitas contactarnos</li>
                    </ul>
                </div>
            </div>
        `;

    const existingDetails = document.getElementById("booking-success-details");
    if (existingDetails) {
      existingDetails.remove();
    }

    const detailsDiv = document.createElement("div");
    detailsDiv.id = "booking-success-details";
    detailsDiv.innerHTML = detailsHtml;
    this.form.insertAdjacentElement("afterend", detailsDiv);

    detailsDiv.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  /**
   * Focus first error field
   */
  focusFirstError() {
    const firstError = this.form.querySelector(".border-red-500");
    if (firstError) {
      firstError.focus();
      firstError.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Set loading state
   */
  setLoadingState(isLoading) {
    this.submitBtn.disabled = isLoading;
    this.btnText.style.display = isLoading ? "none" : "inline";
    this.btnLoading.style.display = isLoading ? "inline" : "none";

    // Add visual feedback
    if (isLoading) {
      this.submitBtn.classList.add("opacity-75", "cursor-not-allowed");
    } else {
      this.submitBtn.classList.remove("opacity-75", "cursor-not-allowed");
    }
  }

  /**
   * Reset form to initial state
   */
  reset() {
    this.form.reset();
    this.setMinDate();
    this.updatePriceEstimate();

    // Clear all errors
    const fields = this.form.querySelectorAll("input, select, textarea");
    fields.forEach((field) => {
      this.clearFieldError(field);
      field.classList.remove("border-green-500");
      field.classList.add("border-stone-300");
    });

    // Hide success details
    const successDetails = document.getElementById("booking-success-details");
    if (successDetails) {
      successDetails.remove();
    }

    this.isSubmitting = false;
    this.setLoadingState(false);
  }
}

// Export the BookingManager class
export { BookingManager };

// Export a default instance for direct use
export default new BookingManager();
